// ====================================================================
// ziven-theme-aura v0.2.9 appearanceSwitcher.js
// ====================================================================
//
// 移动端抽屉里的「外观」行 (主题选择器) —— 辉哥 2026-10-23 23:22 拍板方案 A:
//   行 = 整行可点: 左边一个主题图标 + 中间「外观」 + 右边显示当前模式文字
//   点开 = core 原生底部抽屉 (@media @phone 下 .Dropdown-menu 变 position:fixed 底部面板)
//
// 为什么**自己写小组件**而不是直接用 core 的 ThemeSwitcher:
//   - core ThemeSwitcher (vendor/flarum/core/js/src/forum/components/ThemeSwitcher.tsx)
//     的选项来自 core 的 ThemeMode.colorSchemes 静态数组 = **5 项**
//     (auto / light / dark / light-hc / dark-hc)
//   - ⛔ 那个数组**不能改**: admin 外观页 (AppearancePage) + 用户偏好页 (SettingsPage) 还要用
//   - 辉哥拍板: 本行**只 3 项** (跟随系统 / 浅色 / 深色) ⇒ 这里自己收窄选项列表
//   - 本组件复用 core 的 **SelectDropdown** (下拉容器) + **Button** (选项按钮) + **Icon** (图标)
//     ⇒ 交互 / 键盘导航 / 手机端底部抽屉全走 core 原生, 本包不重造
//
// 切换逻辑 1:1 抄 core ThemeSwitcher.select():
//   app.setColorScheme(scheme)  (内部 documentElement.setAttribute('data-theme', scheme))
//   登录态: app.session.user.savePreferences({ colorScheme: scheme })
//   未登录: sessionStorage.setItem('colorScheme', scheme) (try/catch 包住)
//   最后 m.redraw()
//
// 色值口径 (core ColorScheme 枚举): auto / light / dark
//   ⛔ light-hc / dark-hc 本包**不提供** (辉哥拍板不要「高对比度」两项)
//   ⇒ 但 iconOf() 仍保留兜底: 系统偏好是 high-contrast 时 (app.getSystemColorSchemePreference()
//     会返回 'light-hc'/'dark-hc') 不能渲染成空白图标
//
// 文案:
//   - 行文案「外观」= aura 自己的 locale key (ziiven-theme-aura.forum.appearance_label)
//   - 选项文案 = **aura 自己的 key** ziiven-theme-aura.forum.appearance_{auto,light,dark}_label
//     (辉哥 2026-10-23 23:22 逐条拍板: 「跟随系统 / 浅色 / 深色」—— 一字不差,
//      ⛔ 刻意**不用** core 的「…模式」措辞);
//     core key (core.forum.settings.color_schemes.<scheme>_mode_label) **只作兜底**,
//     aura locale 缺 key 时不会崩 (trans(..., {}, true) 缺 key 返回 key 本身).
// ====================================================================

import app from 'flarum/common/app';
import Button from 'flarum/common/components/Button';
import Icon from 'flarum/common/components/Icon';
import SelectDropdown from 'flarum/common/components/SelectDropdown';

// mithril 是 vendor 注入的 window.m free variable, 不 import (跟 buildElements.js / modifyDrawer.js 一致)

// === 只 3 项 (辉哥 2026-10-23 23:22 拍板) ===
// id 用 core ColorScheme 枚举的值 (auto / light / dark), ⛔ 不含 light-hc / dark-hc
const SCHEMES = [{
  id: 'auto',
  icon: 'fa-solid fa-circle-half-stroke'
}, {
  id: 'light',
  icon: 'fa-regular fa-sun'
}, {
  id: 'dark',
  icon: 'fa-regular fa-moon'
}];

// core ThemeSwitcher.DEFAULT_ICON: 不认识的 scheme (含 light-hc / dark-hc) 的兜底图标
const DEFAULT_ICON = 'fa-solid fa-circle-half-stroke';

// 当前生效的 scheme —— 1:1 抄 core ThemeSwitcher.active()
export function currentScheme() {
  var _ref, _ref2, _app$colorScheme, _app$session$user;
  return (_ref = (_ref2 = (_app$colorScheme = app.colorScheme) != null ? _app$colorScheme : (_app$session$user = app.session.user) == null || (_app$session$user = _app$session$user.preferences()) == null ? void 0 : _app$session$user.colorScheme) != null ? _ref2 : app.forum.attribute('colorScheme')) != null ? _ref : 'auto';
}

// 选项图标 —— core ThemeSwitcher.icon() 的收窄版 (只有 light / dark 两项)
function iconOf(scheme) {
  if (scheme === 'light') return 'fa-regular fa-sun';
  if (scheme === 'dark') return 'fa-regular fa-moon';
  return DEFAULT_ICON;
}

// 选项文案 —— **优先用 aura 自己的 key** (辉哥 2026-10-23 23:22 逐条拍板:
//   「跟随系统 / 浅色 / 深色」—— 一字不差, ⛔ 不要 core 的「…模式」措辞)
// 兜底 = core 现成 key (core.forum.settings.color_schemes.<scheme>_mode_label)
//   ⇒ aura locale 缺 key 时不会崩: trans(..., {}, true) 缺 key 返回 key 本身, 这里识别出来再回退
// ⚠️ 这一个函数同时被**面板里 3 个选项**和**行内右侧当前值**用 ⇒ 改一处两边一起变
export function schemeLabel(scheme) {
  const ownKey = 'ziiven-theme-aura.forum.appearance_' + scheme + '_label';
  const own = app.translator.trans(ownKey, {}, true);

  // trans 缺 key 时返回 key 本身 ⇒ 用它判断 aura key 是否真的存在
  if (own && own !== ownKey) return own;
  return app.translator.trans('core.forum.settings.color_schemes.' + scheme + '_mode_label', {}, true);
}

// 行文案「外观」—— aura 自己的 locale key
export function appearanceLabel() {
  return app.translator.trans('ziiven-theme-aura.forum.appearance_label', {}, true);
}

// 切换 —— 1:1 抄 core ThemeSwitcher.select()
// onChanged: 切换后**重画抽屉 nav** 的回调 (见下面 buildAppearanceNavItem 的说明)
function selectScheme(scheme, onChanged) {
  app.setColorScheme(scheme);
  if (app.session.user) {
    app.session.user.savePreferences({
      colorScheme: scheme
    });
  } else {
    try {
      sessionStorage.setItem('colorScheme', scheme);
    } catch (e) {
      // 浏览器禁了 storage: 本次会话内生效, 离开页面后失效 (core 同款处理)
    }

    // v0.2.20: localStorage 兜底 —— sessionStorage 被禁时也能跨刷新保持 (见文件末尾的守卫段)
    //   显式方案 (浅色/深色) ⇒ 写兜底; 'auto' (跟随系统) ⇒ 删兜底
    //   ⚠️ 两个存储**各自 try/catch**, 互不影响 (一个被禁另一个还能用)
    if (EXPLICIT_SCHEMES.indexOf(scheme) !== -1) {
      writeFallbackScheme(scheme);
    } else {
      clearFallbackScheme();
    }
  }

  // v0.2.20: 同步「显式选择」内存态 + 确保守卫已装 (幂等, 只装一次)
  explicitScheme = EXPLICIT_SCHEMES.indexOf(scheme) !== -1 ? scheme : null;
  installColorSchemeGuard();
  m.redraw();

  // ⚠️ 必须重画 nav (实测踩到的坑):
  //   抽屉里的 nav 是**独立 mithril root** (buildElements.renderNavItems 用 m.render 渲染到 ul),
  //   **不在 app 的组件树里** ⇒ m.redraw() 不会重画它
  //   ⇒ 不重画的话: 行内「当前模式」文字 + 面板里那个 ✓ 都停留在**切换前**的状态 (实测复现)
  //   ⇒ 这里调 onChanged (= buildElements.renderNavItems 同一个容器) 重画整段 nav,
  //      跟 aura 现有的 refreshNavActive (路由变化时重画 nav) 是**同一套机制**.
  //   用 setTimeout(0) 延后: 别在 click 事件派发途中拆掉事件目标 (bootstrap 的 dropdown
  //   关闭逻辑还在 document 层跑) —— 跟 aura 现有 refreshNavActive 的做法一致.
  if (typeof onChanged === 'function') {
    setTimeout(() => {
      try {
        onChanged();
      } catch (e) {
        console.error('[ziven-theme-aura] refresh appearance nav failed:', e);
      }
    }, 0);
  }
}

// === core SelectDropdown 的**本地子类** ===
// 只覆写 getButtonContent, 让 toggle 按钮内容 = [主题图标][外观][当前模式文字]
//   (core 原生是 [图标][当前项 label][caret], 中间没有「外观」两字, 不符合辉哥拍板的行布局)
// ⛔ 不动 core 的 SelectDropdown.prototype: 那是全局对象, 桌面端 core ThemeSwitcher 也在用
//    ⇒ 用子类, 只影响本包这一处
class AppearanceSelectDropdown extends SelectDropdown {
  getButtonContent() {
    const active = currentScheme();
    // 跟 core ThemeSwitcher.view() 一样: auto 时按系统偏好解析出真实图标
    const resolved = active === 'auto' ? app.getSystemColorSchemePreference() : active;
    return [m(Icon, {
      name: iconOf(resolved),
      noStyleOverride: true,
      className: 'Button-icon App-drawer-aura-appearance-icon'
    }), m('span', {
      className: 'Button-label App-drawer-aura-appearance-label'
    }, [m('span', {
      className: 'Button-labelText'
    }, appearanceLabel()), m('span', {
      className: 'Button-labelText App-drawer-aura-appearance-current'
    }, schemeLabel(active))])];
  }
}

// === 造 nav 行 (给 navItems.add 用) ===
// 组件结构 (core 原生, 不重造):
//   <li class="item-aura-appearance">
//     <div class="ButtonGroup Dropdown dropdown App-drawer-aura-appearance Dropdown--select">
//       <button class="Dropdown-toggle Button Button--flat">…</button>
//       <ul class="Dropdown-menu dropdown-menu">
//         <li><button class="Button App-drawer-aura-appearance-option">跟随系统</button></li>
//         …
//       </ul>
//     </div>
//   </li>
// onChanged: 切换配色后重画这段 nav 的回调 (由 buildElements 传入 = renderNavItems 同一个容器)
export function buildAppearanceNavItem(onChanged) {
  const active = currentScheme();
  return m(AppearanceSelectDropdown, {
    key: 'aura-appearance',
    className: 'App-drawer-aura-appearance',
    buttonClassName: 'Button Button--flat App-drawer-aura-appearance-toggle',
    defaultLabel: schemeLabel(active),
    accessibleToggleLabel: app.translator.trans('core.forum.header.theme_switcher_accessible_label', {}, true),
    noStyleOverride: true
  }, SCHEMES.map(scheme => m(Button, {
    className: 'App-drawer-aura-appearance-option',
    // core 规则: 当前项图标换成 fa-check, 其余显示各自主题图标
    // (core ThemeSwitcher 也只靠这个 ✓ 标当前项 —— 它没有给按钮加 .active class)
    icon: active === scheme.id ? 'fa-solid fa-check' : iconOf(scheme.id),
    noStyleOverride: true,
    active: active === scheme.id,
    onclick: () => selectScheme(scheme.id, onChanged)
  }, schemeLabel(scheme.id))));
}

// ====================================================================
// v0.2.20 游客态配色守卫 (辉哥 2026-10-26 12:3x 报障: iPad 未登录切浅色却像深色高对比)
// ====================================================================
// 根因 (vendor 源码 + 生产实测, 详见 CHANGELOG v0.2.20 段):
//   - core 的配色方案有 5 项 (auto / light / dark / light-hc / dark-hc),
//     而 'auto' 在「系统深色 + prefers-contrast: more」的设备上会被解析成 **'dark-hc'**
//     (vendor/flarum/core/js/src/common/Application.tsx L538-546 getSystemColorSchemePreference,
//      L553-561 setColorScheme) ⇒ 未登录态默认「跟随系统」= **深色高对比度** (= 辉哥看到的观感)
//   - 游客态的显式选择**只存在 sessionStorage** (core Application.tsx L517-523, try/catch 吞异常)
//     ⇒ 存储不可用时**每次页面加载都回落 'auto'** ⇒ 又变回 'dark-hc'
//   - core 的 initColorScheme() (L507-535) 还会监听系统 prefers-color-scheme / prefers-contrast
//     的变化并**重跑** ⇒ 把 data-theme 写回 'auto' 的解析结果 ⇒ 游客的显式选择被悄悄抢走
//
// 本段做两件事 (⛔ 不改 UI 口径, ⛔ 不加 core 的「浅色/深色高对比度」两项 —— 辉哥 2026-10-23 23:22 拍板只要 3 项):
//   1) sessionStorage 不可用时**降级用 localStorage** (aura 自己的 key, 不与 core 的 'colorScheme' 冲突)
//   2) 监听 documentElement 的 data-theme 属性: 用户**显式**选过浅色/深色时,
//      若被 core 改回 'auto' 的解析结果, 就**把用户的选择拉回来**
//
// ⚠️ 只对**游客态**生效 (登录态由 core 的服务端偏好负责, 拉回会跟服务端冲突)
// ⚠️ 用户主动选「跟随系统」时**放弃守卫** (否则会挡住 auto)
//
// v0.2.20 订正：存储被禁时保留内存态
//   缺陷: sessionStorage 与 localStorage **都**被浏览器禁用时 (Safari「阻止所有 Cookie」),
//         守卫回调读不到任何显式选择 ⇒ 旧实现一律把内存态 explicitScheme 清零
//         ⇒ 用户刚选的浅色, 在**同一次页面会话内**被 core 改回 'dark-hc' 后拉不回来
//   根因: 代码没区分「读不到存储(被禁)」与「存储里确实没有显式选择」
//   修法: 读取改成**带状态** ({ available, value }) —— available=false 即「被禁」
//         ⇒ 被禁时**保留**内存态 explicitScheme; 存储可读但为空 ⇒ 仍按旧语义清零
// ====================================================================

// core 游客态用的 sessionStorage key (core Application.tsx L519) —— 只读它, 不改它的语义
const CORE_SESSION_KEY = 'colorScheme';

// aura 自己的 localStorage 兜底 key
//   ⛔ 不能用 core 的 'colorScheme' 当 localStorage key (跟 core 的 sessionStorage key 撞名 ⇒ 语义混乱)
const FALLBACK_KEY = 'ziiven-theme-aura.colorScheme';

// 「显式选择」白名单 —— ⛔ 不含 'auto' (auto = 用户主动选了「跟随系统」⇒ 不算显式选择)
//   ⛔ 也不含 'light-hc' / 'dark-hc' (core 自带的高对比度, 辉哥 2026-10-23 23:22 拍板 aura 只要 3 项)
const EXPLICIT_SCHEMES = ['light', 'dark'];

// 内存态: 用户**显式**选的方案 (light / dark), null = 无显式选择 (跟随系统 / 还没选过)
let explicitScheme = null;

// 守卫是否已装 —— 幂等安装, 只装一次
let guardInstalled = false;

// 读 sessionStorage —— 带状态 (v0.2.20 订正)
//   available: true  = getItem **正常返回** (value 可能是 null / '' = 存储里确实没有显式选择)
//   available: false = getItem **抛异常** (存储被禁: SecurityError / QuotaExceededError)
//   ⚠️ 这两种情况**必须区分**: 「读不到」(被禁) ≠ 「没有」(空)
//     旧实现两者都返回 null ⇒ 守卫把内存态的显式选择一起清零 (见下面的守卫段)
//     ⇒ 用户选的浅色在**同一次页面会话内**被 core 抢走后拉不回来
function readSessionSchemeState() {
  try {
    return {
      available: true,
      value: sessionStorage.getItem(CORE_SESSION_KEY)
    };
  } catch (e) {
    // 浏览器禁了 storage ⇒ 读不到 (≠ 存储里没有!)
    return {
      available: false,
      value: null
    };
  }
}

// 读 localStorage 兜底 —— 带状态 (同 readSessionSchemeState)
function readFallbackSchemeState() {
  try {
    return {
      available: true,
      value: localStorage.getItem(FALLBACK_KEY)
    };
  } catch (e) {
    return {
      available: false,
      value: null
    };
  }
}

// 写 localStorage 兜底 —— 单独 try/catch (写失败不影响 sessionStorage 那条路)
function writeFallbackScheme(scheme) {
  try {
    localStorage.setItem(FALLBACK_KEY, scheme);
  } catch (e) {
    // 写不进去 (隐私模式 / 配额满): 本次会话内仍有效, 只是跨刷新保不住
  }
}

// 删 localStorage 兜底 —— 单独 try/catch
function clearFallbackScheme() {
  try {
    localStorage.removeItem(FALLBACK_KEY);
  } catch (e) {
    // 删不掉: 无所谓 (下次读到的旧值仍会过白名单校验)
  }
}

// 解析「用户显式选了什么」—— 顺序: 先 sessionStorage (core 写的那个), 再 localStorage 兜底
//   - sessionStorage 值是 'auto'      ⇒ 返回 null (用户主动选了「跟随系统」⇒ 不算显式选择)
//   - sessionStorage 值在白名单里     ⇒ 用它
//   - sessionStorage 空 / 读不到       ⇒ 回落到 localStorage 兜底值 (白名单校验)
//   - 其它值 (core 原生切换器写的 light-hc / dark-hc) ⇒ 返回 null (用户自己选的, aura 不跟它抢)
// ⚠️ 对外语义不变: 只返回 'light' / 'dark' / null
//   (v0.2.20 订正: 内部改用**带状态**的读取 —— 「被禁」与「为空」分开处理,
//    但**解析结果**与旧实现一致: 两者都回落 localStorage 兜底)
function resolveExplicitScheme() {
  const sState = readSessionSchemeState();
  if (sState.available) {
    // session 存储**可读** ⇒ 存储说了算
    if (sState.value === 'auto') return null;
    if (EXPLICIT_SCHEMES.indexOf(sState.value) !== -1) return sState.value;

    // 非空但不在白名单 (core 原生 light-hc / dark-hc) ⇒ 不跟 core 抢, **不**回落兜底
    if (sState.value !== null && sState.value !== '') return null;

    // 可读但为空 ⇒ 回落到 localStorage 兜底
    const fState = readFallbackSchemeState();
    if (fState.available && EXPLICIT_SCHEMES.indexOf(fState.value) !== -1) return fState.value;
    return null;
  }

  // session 存储**被禁** ⇒ 回落到 localStorage 兜底 (兜底也被禁 ⇒ null)
  const fState = readFallbackSchemeState();
  if (fState.available && EXPLICIT_SCHEMES.indexOf(fState.value) !== -1) return fState.value;
  return null;
}

// 守卫: 监听 documentElement 的 data-theme
//   core 把游客的显式选择改回「auto 的解析结果」时, 把用户的选择拉回来
function installColorSchemeGuard() {
  if (guardInstalled) return;
  guardInstalled = true;
  const observer = new MutationObserver(() => {
    // 登录态: 服务端偏好为准 ⇒ 不守 (拉回会跟服务端冲突)
    if (app.session && app.session.user) return;

    // v0.2.20 订正: 用**带状态**的读取 —— 必须区分「读不到(被禁)」与「读到了但是空」
    const sState = readSessionSchemeState();

    // (A) 用户主动选了「跟随系统」⇒ 放弃守卫 (清显式选择 + 清 localStorage 兜底)
    if (sState.available && sState.value === 'auto') {
      explicitScheme = null;
      clearFallbackScheme();
      return;
    }
    if (sState.available && EXPLICIT_SCHEMES.indexOf(sState.value) !== -1) {
      // (B) sessionStorage 里有显式选择 ⇒ 记下 + 写 localStorage 兜底
      //   (这样**连 core 原生顶栏切换器**那条路也能拿到兜底 —— 那条路只写 sessionStorage)
      explicitScheme = sState.value;
      writeFallbackScheme(sState.value);
    } else if (sState.available && sState.value !== null && sState.value !== '') {
      // (C) sessionStorage 里是 core 原生的 light-hc / dark-hc ⇒ 放弃守卫, 不跟 core 抢
      explicitScheme = null;
      return;
    } else {
      // (D) sessionStorage 里没有可用的显式选择 (或 session 存储被禁) ⇒ 试 localStorage 兜底
      const fState = readFallbackSchemeState();
      if (fState.available && EXPLICIT_SCHEMES.indexOf(fState.value) !== -1) {
        // (D1) 兜底里有显式选择 ⇒ 用它
        explicitScheme = fState.value;
      } else if (sState.available) {
        // (D2-a) session 存储**可读但为空** = 确实没选过 ⇒ 存储说了算 ⇒ 清零
        explicitScheme = null;
      }
      // (D2-b) ⭐ session 存储**被禁** ⇒ **保留**当前内存态 explicitScheme
      //   (旧实现在这里一律清零 ⇒ 用户选的浅色在同一次页面会话内被 core 抢走后拉不回来;
      //    v0.2.20 订正: 存储读不到 ≠ 用户没选过 ⇒ 保留内存态, 让守卫继续把选择拉回来)
    }
    if (!explicitScheme) return;
    const current = document.documentElement.getAttribute('data-theme');

    // 当前已经是显式值 ⇒ 不动
    //   ⛔ 防死循环: 拉回后 data-theme 变成显式值 ⇒ 回调再触发时这里就返回
    if (current === explicitScheme) return;
    app.setColorScheme(explicitScheme);
  });
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['data-theme']
  });
}

// 扩展 initializer 入口 —— 恢复游客态被记住的显式配色选择
//   - 装守卫 (幂等)
//   - 解析出显式选择 ⇒ 记下, 并**延后** (setTimeout 0) 应用一次
// ⚠️ 必须延后: core 的 initColorScheme() 跑在**扩展 initializer 之后**
//   (vendor/flarum/core/js/src/common/Application.tsx boot(): L378 initialize() → L500 initColorScheme())
//   ⇒ initializer 里直接应用会被 core 随后覆盖 ⇒ 只能延后 (或靠上面的守卫兜住)
export function applyStoredScheme() {
  installColorSchemeGuard();
  setTimeout(() => {
    try {
      // 登录态: 服务端偏好为准 ⇒ 不管 (core 用的是服务端偏好, 不是 sessionStorage)
      if (app.session && app.session.user) return;
      const stored = resolveExplicitScheme();
      if (!stored) return;
      explicitScheme = stored;
      app.setColorScheme(stored);
    } catch (e) {
      console.error('[ziven-theme-aura] apply stored color scheme failed:', e);
    }
  }, 0);
}